import { MapOverlayConfig } from '@/map/types/overlay';
import { Badge } from '@/ui/components/ui/badge';
import { Switch } from '@/ui/components/ui/switch';
import { Label } from '@/ui/components/ui/label';
import { AlertCircle, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface MapOverlayControlsProps {
  config: MapOverlayConfig | null;
  currentZoom: number;
  isInBounds: boolean;
  isEnabled: boolean;
  onToggle: (enabled: boolean) => void;
}

export const MapOverlayControls = ({
  config,
  currentZoom,
  isInBounds,
  isEnabled,
  onToggle
}: MapOverlayControlsProps) => {
  const { t } = useTranslation('map');
  const [showZoomHint, setShowZoomHint] = useState(false);
  const [showBoundsHint, setShowBoundsHint] = useState(false);

  useEffect(() => {
    if (!config || !isEnabled) {
      setShowZoomHint(false);
      setShowBoundsHint(false);
      return;
    }

    const isZoomInRange = currentZoom >= config.min_zoom && currentZoom <= config.max_zoom;
    setShowZoomHint(!isZoomInRange);
    setShowBoundsHint(!isInBounds && isZoomInRange);
  }, [config, currentZoom, isInBounds, isEnabled]);

  if (!config) return null;

  return (
    <div className="z-10 flex flex-col gap-2">
      <div className="flex items-center gap-3 bg-background border border-border rounded-md px-3 py-2 shadow-md">
        <Badge variant="secondary" className="shadow-sm">
          <span className="text-xs font-medium">{t('overlay.title', { version: config.version })}</span>
        </Badge>
        <div className="flex items-center gap-2">
          <Switch
            id="overlay-toggle"
            checked={isEnabled}
            onCheckedChange={onToggle}
            className="data-[state=checked]:bg-primary"
          />
          <Label htmlFor="overlay-toggle" className="text-xs font-medium cursor-pointer">
            {isEnabled ? t('overlay.on') : t('overlay.off')}
          </Label>
        </div>
      </div>

      {isEnabled && showZoomHint && (
        <div className="flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md px-3 py-2 shadow-md max-w-xs">
          <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0" />
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            {t('overlay.zoomHint', { min: config.min_zoom, max: config.max_zoom })}
          </p>
        </div>
      )}

      {isEnabled && showBoundsHint && (
        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2 shadow-md max-w-xs">
          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500 flex-shrink-0" />
          <p className="text-xs text-blue-800 dark:text-blue-200">
            {t('overlay.boundsHint')}
          </p>
        </div>
      )}
    </div>
  );
};
